/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseMutationReturnType } from '@opengeoweb/api';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import { deleteViewPreset, saveViewPreset, saveViewPresetAs } from './api';
import { viewpresetQueryOptions } from './queryOptions';
import type { ViewPreset } from '../../store/viewPresets/types';
import type {
  ViewPresetListItem,
  ViewTemplatePreset,
} from '../../store/workspace/types';
import { workspaceQueryOptions } from '../workspace-api/queryOptions';

export const useViewTemplatePresets = (): UseQueryResult<
  ViewTemplatePreset[]
> =>
  useQuery({
    ...workspaceQueryOptions.getTemplatePresets(),
    select: (response) => response.viewPresets,
  });

export const useDeleteViewPreset = (): UseMutationReturnType<
  Headers | null,
  { id: string }
> => {
  const { auth } = useAuthenticationContext();

  return useMutation({
    mutationFn: async ({ id }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return deleteViewPreset(id, auth);
    },
  });
};

export const useSaveViewPreset = (): UseMutationReturnType<
  void,
  { id: string; data: ViewPreset }
> => {
  const { auth } = useAuthenticationContext();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return saveViewPreset(id, data, auth);
    },
  });
};

export const useSaveViewPresetAs = (): UseMutationReturnType<
  string,
  { data: ViewPreset }
> => {
  const { auth } = useAuthenticationContext();

  return useMutation({
    mutationFn: async ({ data }) => {
      if (!auth) {
        throw new Error('Authentication context not found');
      }
      return saveViewPresetAs(data, auth);
    },
  });
};

export const useViewPreset = (id?: string): UseQueryResult<ViewPreset> => {
  const { auth } = useAuthenticationContext();
  return useQuery(viewpresetQueryOptions.getViewPreset(id, auth!));
};

export const useViewPresets = (): UseQueryResult<ViewPresetListItem[]> => {
  const { auth } = useAuthenticationContext();
  return useQuery(viewpresetQueryOptions.getViewPresets(auth!));
};
